import { ChevronRight } from "lucide-react";

import useTranslations from "@/shared/lang/useTranslations";

import { DEFAULT_POCKET_COLOR, resolvePocketName } from "../constants/pockets.constants";
import type { Pocket } from "../types/pockets-api.types";
import { PocketIcon } from "./PocketIcon";

type PocketRowProps = {
  pocket: Pocket;
  onOpen: (pocketId: string) => void;
};

const formatUSD = (raw: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(raw);

// `pocket.balance` is the DB-cached value synced via webhooks; the detail
// page hits `/internal/pockets/:id/balance` for the live provider value.
export const PocketRow = ({ pocket, onOpen }: PocketRowProps) => {
  const { t } = useTranslations("cards");
  const name = resolvePocketName(pocket);
  const color = pocket.metadata?.color || DEFAULT_POCKET_COLOR;
  const cardsCount = pocket.cards_count ?? 0;
  const monthSpend = pocket.month_spend_usd ?? 0;
  const balance =
    typeof pocket.balance === "number" ? pocket.balance : parseFloat(pocket.balance as string) || 0;

  return (
    <button
      type="button"
      onClick={() => onOpen(pocket.id)}
      className="flex w-full items-center gap-4 rounded-[var(--radius-lg)] border border-border bg-card p-4 text-left transition-all hover:border-ring/40 hover:shadow-[var(--shadow-card)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <PocketIcon icon={pocket.metadata?.icon} color={color} size={40} />

      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">{name}</div>
        <div className="text-[11px] text-muted-foreground">
          {cardsCount} {t("identities_label_cards").toLowerCase()}
        </div>
      </div>

      <div className="hidden w-[140px] shrink-0 text-right md:block">
        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t("cards_label_balance_pocket")}
        </div>
        <div className="text-sm font-semibold tabular-nums text-foreground">{formatUSD(balance)}</div>
      </div>

      <div className="hidden w-[140px] shrink-0 text-right md:block">
        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t("cards_label_month_spend")}
        </div>
        <div className="font-mono text-sm tabular-nums text-foreground">{formatUSD(monthSpend)}</div>
      </div>

      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
    </button>
  );
};
